import { useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import {
  FiAlertTriangle,
  FiInfo,
  FiPlay,
  FiRefreshCw,
} from "react-icons/fi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import * as VertisGsmaService from "../../../../service/Ir21OndemandSyncService";

/**
 * @author Guilherme Almeida
 * @date 02/09/2021
 */
export default function Ir21OndemandSync() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleRunIr21Missing() {
    Swal.fire({
      icon: "warning",
      title: "Iniciar sincronização?",
      text: "Os registros IR.21 serão sincronizados em segundo plano.",
      showCancelButton: true,
      confirmButtonColor: "#DD6B55",
      confirmButtonText: "Iniciar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        setIsSubmitting(true);
        VertisGsmaService.run()
          .then(() =>
            toast.success(
              "Execução iniciada. O processamento ocorrerá em segundo plano.",
            ),
          )
          .catch((e) => {
            toast.error(
              e?.response?.data?.message ||
                e?.response?.data ||
                e.message ||
                "Erro inesperado ao iniciar a execução.",
            );
          })
          .finally(() => setIsSubmitting(false));
      }
    });
  }

  return (
    <Row>
      <Col>
        <div style={styles.pageShell}>
          <section style={styles.card}>
            <header style={styles.hero}>
              <div style={styles.heroIcon}>
                <FiRefreshCw />
              </div>
              <div style={styles.heroCopy}>
                <h2 style={styles.title}>GSMA - On Demand Sync</h2>
                <div style={styles.titleLine} />
              </div>
            </header>

            <div style={styles.content}>
              <p style={styles.paragraph}>
                A funcionalidade <strong>GSMA - On Demand Sync</strong> realiza
                a sincronização da base de dados do Roaming Audit Tool com os
                três registros mais recentes de <strong>IR.21</strong> de todas
                as operadoras (TADIGs) associadas à operadora selecionada.
              </p>

              <p style={styles.paragraph}>
                Para executar o processo, o sistema utiliza as configurações
                previamente definidas na funcionalidade{" "}
                <strong>GSMA - Automatic Sync Setup</strong>.
              </p>

              <div style={styles.infoBox}>
                <FiInfo style={styles.infoIcon} />
                <div>
                  <strong style={styles.infoTitle}>
                    Execução em segundo plano (background)
                  </strong>
                  <p style={styles.infoText}>
                    A sincronização é executada em segundo plano (background) e
                    pode demandar um tempo considerável para conclusão,
                    dependendo do volume de dados a ser processado.
                  </p>
                  <p style={styles.infoText}>
                    Durante a execução, os registros serão atualizados
                    gradualmente na base de dados à medida que o processamento
                    for avançando.
                  </p>
                </div>
              </div>

              <div style={styles.confirmRow}>
                <div style={styles.warningIconWrap}>
                  <FiAlertTriangle />
                </div>
                <strong style={styles.confirmText}>
                  Deseja confirmar a execução da sincronização?
                </strong>
              </div>

              <Button
                style={styles.actionButton}
                className="btn btn-primary"
                type="button"
                onClick={() => handleRunIr21Missing()}
                disabled={isSubmitting}
              >
                <FiPlay style={styles.playIcon} />
                {isSubmitting
                  ? "Iniciando sincronização..."
                  : "Executar Sincronização"}
              </Button>
            </div>
          </section>
        </div>
      </Col>
    </Row>
  );
}

const styles = {
  pageShell: {
    padding: "18px 22px",
  },
  card: {
    width: "100%",
    maxWidth: 1160,
    margin: "0 auto",
    padding: "24px 32px 24px",
    borderRadius: 10,
    border: "1px solid #e4e9f1",
    background: "#fff",
    boxShadow: "0 16px 34px rgba(15, 35, 65, 0.12)",
    color: "#101a3d",
  },
  hero: {
    display: "flex",
    alignItems: "center",
    gap: 32,
  },
  heroIcon: {
    width: 72,
    height: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    borderRadius: 10,
    background: "#e7f1ff",
    color: "#0958c3",
    fontSize: 42,
  },
  heroCopy: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: "0 0 14px",
    color: "#101a3d",
    fontSize: 25,
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: 0,
  },
  titleLine: {
    height: 1,
    width: "100%",
    background: "#d9dfe9",
  },
  content: {
    width: "100%",
    paddingTop: 24,
  },
  paragraph: {
    maxWidth: 980,
    margin: "0 0 20px",
    color: "#101a3d",
    fontSize: 16,
    lineHeight: 1.5,
    fontWeight: 500,
  },
  infoBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: 24,
    margin: "2px 0 24px",
    padding: "22px 26px",
    borderRadius: 8,
    background: "#eef5ff",
  },
  infoIcon: {
    flex: "0 0 auto",
    marginTop: 4,
    color: "#075dc5",
    fontSize: 44,
  },
  infoTitle: {
    display: "block",
    marginBottom: 8,
    color: "#075dc5",
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.25,
  },
  infoText: {
    margin: "0 0 14px",
    color: "#101a3d",
    fontSize: 15,
    lineHeight: 1.5,
  },
  confirmRow: {
    display: "flex",
    alignItems: "center",
    gap: 24,
    padding: "16px 8px",
    marginBottom: 12,
    borderTop: "1px solid #d9dfe9",
    borderBottom: "1px solid #d9dfe9",
  },
  warningIconWrap: {
    width: 48,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    borderRadius: "50%",
    background: "#fff0cf",
    color: "#f0a000",
    fontSize: 28,
  },
  confirmText: {
    color: "#101a3d",
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.35,
  },
  actionButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    width: "100%",
    minHeight: 54,
    border: 0,
    borderRadius: 6,
    background: "#045ec9",
    boxShadow: "0 8px 16px rgba(4, 91, 190, 0.24)",
    fontSize: 16,
    fontWeight: 800,
    lineHeight: 1.2,
  },
  playIcon: {
    flex: "0 0 auto",
    fontSize: 22,
  },
};
